import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, QrCode, ArrowRight, Clock, ScanLine } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import jsQR from 'jsqr';
import { isCapacitorApp, scanQrCodeFromCamera } from '../utils/cameraHelper';
import useEscapeKey from '../hooks/useEscapeKey';

const QrPaymentLinkModal = ({ isOpen, onClose }) => {
  useEscapeKey(onClose, isOpen);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [qrLinkInput, setQrLinkInput] = useState('');
  const [history, setHistory] = useState([]); // { url, openedAt }

  const scanQrCodeLiveWithCancel = async () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return null;
    }

    const mediaDevices = navigator?.mediaDevices;
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
      toast.error('Tu dispositivo no soporta cámara en este navegador');
      return null;
    }

    let overlay = null;
    let video = null;
    let canvas = null;
    let ctx = null;
    let stream = null;
    let rafId = null;
    let finished = false;

    const cleanup = () => {
      if (finished) return;
      finished = true;

      try {
        if (rafId != null) {
          window.cancelAnimationFrame(rafId);
        }
      } catch {}

      try {
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch {}

      try {
        if (overlay && overlay.parentNode) {
          overlay.parentNode.removeChild(overlay);
        }
      } catch {}

      try {
        if (video && typeof video.remove === 'function') {
          video.remove();
        }
      } catch {}
    };

    const buildOverlay = () => {
      overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.inset = '0';
      overlay.style.background = 'rgba(0,0,0,0.86)';
      overlay.style.zIndex = '9999';
      overlay.style.display = 'flex';
      overlay.style.alignItems = 'center';
      overlay.style.justifyContent = 'center';
      overlay.style.padding = '16px';

      const frame = document.createElement('div');
      frame.style.position = 'relative';
      frame.style.width = '100%';
      frame.style.maxWidth = '420px';
      frame.style.aspectRatio = '1 / 1';
      frame.style.borderRadius = '16px';
      frame.style.overflow = 'hidden';
      frame.style.border = '1px solid rgba(255,255,255,0.15)';
      frame.style.background = 'rgba(0,0,0,0.35)';

      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.setAttribute('aria-label', 'Cerrar escáner');
      closeBtn.style.position = 'absolute';
      closeBtn.style.top = '10px';
      closeBtn.style.right = '10px';
      closeBtn.style.width = '36px';
      closeBtn.style.height = '36px';
      closeBtn.style.borderRadius = '9999px';
      closeBtn.style.border = '1px solid rgba(255,255,255,0.18)';
      closeBtn.style.background = 'rgba(0,0,0,0.45)';
      closeBtn.style.color = 'rgba(255,255,255,0.9)';
      closeBtn.style.display = 'flex';
      closeBtn.style.alignItems = 'center';
      closeBtn.style.justifyContent = 'center';
      closeBtn.style.cursor = 'pointer';
      closeBtn.innerText = '✕';
      closeBtn.onclick = () => {
        cleanup();
      };

      const hint = document.createElement('div');
      hint.style.position = 'absolute';
      hint.style.left = '12px';
      hint.style.right = '12px';
      hint.style.bottom = '12px';
      hint.style.padding = '10px 12px';
      hint.style.borderRadius = '12px';
      hint.style.background = 'rgba(0,0,0,0.55)';
      hint.style.border = '1px solid rgba(255,255,255,0.12)';
      hint.style.color = 'rgba(255,255,255,0.85)';
      hint.style.fontSize = '12px';
      hint.style.lineHeight = '16px';
      hint.innerText = 'Apunta la cámara al código QR. Puedes cerrar con ✕.';

      video = document.createElement('video');
      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;
      video.style.width = '100%';
      video.style.height = '100%';
      video.style.objectFit = 'cover';

      frame.appendChild(video);
      frame.appendChild(closeBtn);
      frame.appendChild(hint);
      overlay.appendChild(frame);
      document.body.appendChild(overlay);
    };

    const requestCamera = async () => {
      stream = await mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });
      video.srcObject = stream;
      await new Promise((resolve) => {
        const onLoaded = () => resolve();
        video.addEventListener('loadedmetadata', onLoaded, { once: true });
        if (video.readyState >= 1) {
          resolve();
        }
      });
    };

    const ensureCanvas = () => {
      if (!canvas) {
        canvas = document.createElement('canvas');
      }
      const w = video.videoWidth || 0;
      const h = video.videoHeight || 0;
      if (!w || !h) return false;
      canvas.width = w;
      canvas.height = h;
      ctx = canvas.getContext('2d');
      return !!ctx;
    };

    return await new Promise(async (resolve) => {
      try {
        buildOverlay();
        await requestCamera();

        const startedAt = window.performance?.now ? window.performance.now() : Date.now();
        const maxDurationMs = 15000;

        const tick = () => {
          if (finished) {
            resolve(null);
            return;
          }

          const now = window.performance?.now ? window.performance.now() : Date.now();
          if (now - startedAt > maxDurationMs) {
            cleanup();
            resolve(null);
            return;
          }

          try {
            if (ensureCanvas()) {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
              const code = jsQR(imageData.data, imageData.width, imageData.height);
              const text = code?.data && typeof code.data === 'string' ? code.data.trim() : '';
              if (text) {
                cleanup();
                resolve(text);
                return;
              }
            }
          } catch {
            // ignore frame errors
          }

          rafId = window.requestAnimationFrame(tick);
        };

        rafId = window.requestAnimationFrame(tick);

        const originalCleanup = cleanup;
        cleanup = () => {
          originalCleanup();
          resolve(null);
        };
      } catch (err) {
        console.error('Error escaneando QR (live):', err);
        cleanup();
        resolve(null);
      }
    });
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Cargar historial desde localStorage cuando se abre el modal
  useEffect(() => {
    if (!isOpen || !user?.id || typeof window === 'undefined') return;
    try {
      const key = `qr_payment_links_history_${user.id}`;
      const raw = window.localStorage.getItem(key);
      if (!raw) {
        setHistory([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setHistory(parsed);
      }
    } catch {
      setHistory([]);
    }
  }, [isOpen, user?.id]);

  const persistHistory = (next) => {
    setHistory(next);
    if (!user?.id || typeof window === 'undefined') return;
    try {
      const key = `qr_payment_links_history_${user.id}`;
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // ignore storage failures
    }
  };

  const registerOpenedLink = (urlString) => {
    const entry = {
      url: urlString,
      openedAt: new Date().toISOString(),
    };

    setHistory((prev) => {
      const withoutDup = prev.filter((item) => item.url !== urlString);
      const next = [entry, ...withoutDup].slice(0, 5);
      persistHistory(next);
      return next;
    });
  };

  const handleOpenLink = async (overrideUrl, options = {}) => {
    const raw = ((overrideUrl != null ? overrideUrl : qrLinkInput) || '').trim();

    if (!raw) {
      toast.error('Pega el link de pago QR primero');
      return;
    }
    try {
      const base = typeof window !== 'undefined' ? window.location.origin : undefined;
      let nextUrlString = raw;
      let url = new URL(nextUrlString, base);

      if (options && options.source === 'scan') {
        try {
          if (typeof window !== 'undefined') {
            const shortCodeMatch = (url.pathname || '').match(/^\/(\d{6})$/);

            if (shortCodeMatch) {
              const code = shortCodeMatch[1];
              const resolved = await axios.get(`/api/short-links/resolve/${code}`);
              const meta = resolved?.data?.metadata || {};

              const resolvedTarget = resolved?.data?.target_url;
              if (resolvedTarget && typeof resolvedTarget === 'string') {
                nextUrlString = resolvedTarget;
                url = new URL(nextUrlString, window.location.origin);
              }

              if (meta && meta.type === 'gran_mundo_offer' && meta.offer_id) {
                try {
                  window.localStorage.setItem(
                    'ogm_last_scan',
                    JSON.stringify({
                      offer_id: meta.offer_id,
                      scope: meta.scope || null,
                      store_id: meta.store_id || null,
                      short_code: code,
                      scanned_at: Date.now()
                    })
                  );
                } catch {}
              }
            }
          }
        } catch {}
      }

      if (typeof window !== 'undefined') {
        const isSameOrigin = url.origin === window.location.origin;
        const host = url.hostname || '';
        const isMundoXyzHost = host.includes('mundoxyz');

        const isRelativePath = nextUrlString && typeof nextUrlString === 'string' && nextUrlString.trim().startsWith('/');

        // En app (Capacitor) y link de MundoXYZ: navegar dentro de la app
        if (isCapacitorApp() && isMundoXyzHost) {
          onClose();
          registerOpenedLink(url.toString());
          navigate(url.pathname + url.search + url.hash);
          return;
        }

        if (isRelativePath) {
          onClose();
          registerOpenedLink(url.toString());
          navigate(url.pathname + url.search + url.hash);
          return;
        }

        // Misma origin en web: SPA navigate
        if (isSameOrigin) {
          onClose();
          registerOpenedLink(url.toString());
          navigate(url.pathname + url.search + url.hash);
          return;
        }

        // Otros dominios: abrir en navegador externo / pestaña
        onClose();
        registerOpenedLink(url.toString());
        window.open(url.toString(), '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Invalid QR payment link:', err);
      toast.error('Link de pago QR inválido');
    }
  };

  const handleScan = async () => {
    try {
      if (typeof window === 'undefined') {
        toast.error('Escáner de QR no disponible en este entorno');
        return;
      }

      const scanned = isCapacitorApp() ? await scanQrCodeFromCamera() : await scanQrCodeLiveWithCancel();

      if (!scanned) {
        toast.error('No se detectó ningún código QR válido');
        return;
      }

      // Normalizar contenido escaneado: a veces puede venir la URL y el id en líneas separadas
      const rawText = String(scanned).trim();
      const parts = rawText.split(/\s+/).filter(Boolean);
      let finalUrl = rawText;

      if (parts.length > 1) {
        const urlPart = parts.find((p) => p.startsWith('http://') || p.startsWith('https://'));
        const possibleId = parts.find((p) => /[0-9a-fA-F-]{16,}/.test(p));

        if (urlPart && possibleId && !urlPart.endsWith(possibleId)) {
          try {
            const u = new URL(urlPart);
            let pathname = u.pathname || '';
            if (!pathname.endsWith('/')) {
              pathname += '/';
            }
            pathname += possibleId;
            u.pathname = pathname;
            finalUrl = u.toString();
          } catch {
            finalUrl = urlPart;
          }
        } else if (urlPart) {
          finalUrl = urlPart;
        }
      }

      setQrLinkInput(finalUrl);
      await handleOpenLink(finalUrl, { source: 'scan' });
    } catch (error) {
      console.error('Error en escaneo de QR:', error);
      toast.error('No se pudo escanear el código QR');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="w-full max-w-md card-glass p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-fire-orange/20 flex items-center justify-center">
                  <QrCode size={20} className="text-fire-orange" />
                </div>
                <h2 className="text-lg font-bold">Pagar con QR (Fuegos)</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-glass-hover rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-xs text-text/60 mb-3">
              Pega el link de pago QR que recibiste (por ejemplo desde el POS o un comercio) para abrir
              la pantalla de pago sin salir de donde estás.
            </p>

            <div className="flex flex-col sm:flex-row gap-2 mt-1">
              <input
                type="text"
                value={qrLinkInput}
                onChange={(e) => setQrLinkInput(e.target.value)}
                className="input-glass flex-1 text-[11px]"
                placeholder="https://.../store/mi-tienda/qr/qrSessionId"
              />
              <button
                type="button"
                onClick={handleOpenLink}
                className="px-4 py-2 rounded-lg bg-accent/20 text-accent hover:bg-accent/30 text-[11px] whitespace-nowrap flex items-center gap-1"
              >
                Usar link
                <ArrowRight size={14} />
              </button>
              <button
                type="button"
                onClick={handleScan}
                className="px-4 py-2 rounded-lg bg-glass text-text hover:bg-glass-hover text-[11px] whitespace-nowrap flex items-center gap-1"
              >
                Escanear
                <ScanLine size={14} />
              </button>
            </div>

            {history.length > 0 && (
              <div className="mt-4 border-t border-glass pt-3">
                <div className="flex items-center gap-2 mb-2">
                  <Clock size={14} className="text-text/60" />
                  <span className="text-[11px] text-text/60">Links recientes</span>
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto text-[11px]">
                  {history.map((item) => {
                    let label = item.url;
                    try {
                      const u = new URL(item.url);
                      const path = u.pathname || '';
                      const segments = path.split('/').filter(Boolean);

                      // Detectar patrón /store/:slug/qr/:qrSessionId
                      const storeIndex = segments.indexOf('store');
                      let prefix = '';
                      if (storeIndex !== -1 && segments.length > storeIndex + 1) {
                        const slug = segments[storeIndex + 1];
                        prefix = `Tienda: ${slug} · `;
                      }

                      label = prefix + path + (u.search || '');
                    } catch {
                      // usar url cruda si no se puede parsear
                    }
                    const openedLabel = item.openedAt
                      ? new Date(item.openedAt).toLocaleString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '';

                    return (
                      <div
                        key={item.url + item.openedAt}
                        className="flex items-center justify-between gap-2 glass-panel px-2 py-1"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-text/80">{label}</div>
                          {openedLabel && (
                            <div className="text-[10px] text-text/50">{openedLabel}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setQrLinkInput(item.url);
                            handleOpenLink();
                          }}
                          className="px-2 py-1 rounded-full bg-glass hover:bg-glass-hover text-[10px] whitespace-nowrap"
                        >
                          Abrir
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default QrPaymentLinkModal;